import React, { useState } from 'react';
import { Plus, Trash2, Mail, ChevronDown, ChevronUp, AlertCircle, Tag, ClipboardCopy, Check, Eye, EyeOff } from 'lucide-react';

const EmailTemplatesStep = ({ formData, updateFormData }) => {
    const [expandedTemplate, setExpandedTemplate] = useState(null);
    const [showNewTemplateForm, setShowNewTemplateForm] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [previewIds, setPreviewIds] = useState(new Set());

    const togglePreview = (id) =>
        setPreviewIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const emailTemplates = formData.emailTemplates || [];
    const categories = formData.participantCategories || [];

    const buildPrompt = (template) => {
        const triggerLabel = {
            registration: 'On Registration (sent automatically when a participant registers)',
            payment: 'On Payment Confirmation (sent after successful payment)',
            checkin: 'On Check-in (sent when participant checks in at the event)',
            manual: 'Manual Send (sent manually by the organizer)',
            reminder: 'Event Reminder',
            update: 'Event Update',
        }[template.triggerEvent] || template.triggerEvent;

        const categoryName = template.categoryId
            ? categories.find(c => c.id === template.categoryId)?.name
            : null;

        const fields = formData.participantFields || [];
        const fieldLines = fields.map(f => `  - ${f.fieldName} (${f.fieldType}${f.validation === 'required' ? ', required' : ''})`).join('\n');

        const variables = [
            '{{eventName}} — Event name',
            '{{eventDate}} — Event start date',
            '{{eventAddress}} — Event venue/address',
            '{{registrationId}} — Participant registration ID',
            '{{publicLink}} — Link to view the participant\'s digital ID card',
            ...fields.map(f => `{{${f.fieldName}}} — ${f.fieldName}`),
        ].join('\n  ');

        return `You are helping me write an HTML email template for an event management system.

EVENT DETAILS:
  - Event Name: ${formData.eventName || '[Event Name]'}
  - Event Type: ${formData.eventType === 'paid' ? 'Paid' : 'Free'}
  - Start Date: ${formData.eventStartDate || '[date]'}
  - End Date: ${formData.eventEndDate || '[date]'}
  - Address: ${formData.eventAddress || '[venue address]'}
  - Trigger: ${triggerLabel}${categoryName ? `\n  - Audience: Only participants in the "${categoryName}" category` : ''}

PARTICIPANT FIELDS (data we collect at registration):
${fieldLines || '  - name (text, required)'}

AVAILABLE TEMPLATE VARIABLES (wrap in double curly braces exactly as shown):
  ${variables}

REQUIREMENTS:
  - Professional, clean HTML email (max-width 600px, inline CSS only)
  - Mobile responsive
  - Greeting using {{name}} or {{${fields[0]?.fieldName || 'name'}}}
  - Mention event name with {{eventName}}
  - Show registration ID {{registrationId}} prominently (if trigger is registration)
  - Include a call-to-action button linking to {{publicLink}} — "View My ID Card"
  - Warm, professional tone matching the event context
  - A footer with event date and address

OUTPUT FORMAT — reply with exactly these three sections:

SUBJECT:
[write the subject line here]

HTML:
[write the complete HTML email body here — inline CSS, no <html>/<body> wrapper]

TEXT:
[write the plain-text fallback here]`;
    };

    const copyPrompt = async (template) => {
        const prompt = buildPrompt(template);
        try {
            await navigator.clipboard.writeText(prompt);
            setCopiedId(template.id);
            setTimeout(() => setCopiedId(null), 2500);
        } catch {
            // Fallback for older browsers
            const ta = document.createElement('textarea');
            ta.value = prompt;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            setCopiedId(template.id);
            setTimeout(() => setCopiedId(null), 2500);
        }
    };

    const addTemplate = () => {
        const newTemplate = {
            id: `template-${Date.now()}`,
            name: 'New Template',
            from: formData.emailConfig?.email || '',
            to: [],
            cc: [],
            bcc: [],
            subject: '',
            htmlBody: '',
            textBody: '',
            enabled: true,
            triggerEvent: 'manual'
        };

        updateFormData({
            emailTemplates: [...emailTemplates, newTemplate]
        });

        setExpandedTemplate(newTemplate.id);
        setShowNewTemplateForm(false);
    };

    const updateTemplate = (id, updates) => {
        updateFormData({
            emailTemplates: emailTemplates.map(t =>
                t.id === id ? { ...t, ...updates } : t
            )
        });
    };

    const deleteTemplate = (id) => {
        if (window.confirm('Are you sure you want to delete this email template?')) {
            updateFormData({
                emailTemplates: emailTemplates.filter(t => t.id !== id)
            });
            if (expandedTemplate === id) {
                setExpandedTemplate(null);
            }
        }
    };

    const handleArrayInput = (templateId, field, value) => {
        const emails = value.split(',').map(e => e.trim()).filter(e => e);
        updateTemplate(templateId, { [field]: emails });
    };

    const triggerEventOptions = [
        { value: 'manual', label: 'Manual Send' },
        { value: 'registration', label: 'On Registration' },
        { value: 'payment', label: 'On Payment Confirmation' },
        { value: 'reminder', label: 'Event Reminder' },
        { value: 'update', label: 'Event Update' }
    ];

    if (!formData.emailConfig?.enabled) {
        return (
            <div className="space-y-6">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Email Templates</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Create and manage email templates for automated communications
                    </p>
                </div>

                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-8 text-center">
                    <AlertCircle className="w-12 h-12 text-amber-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Email Configuration Required
                    </h3>
                    <p className="text-gray-600">
                        Please enable and configure email settings in the Email Configuration step before creating templates.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Email Templates</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Create and manage email templates for automated communications
                    </p>
                </div>
                <button
                    type="button"
                    onClick={addTemplate}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Template
                </button>
            </div>

            {/* Templates List */}
            {emailTemplates.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                    <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No email templates created yet</p>
                    <button
                        type="button"
                        onClick={addTemplate}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Create Your First Template
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {emailTemplates.map((template) => (
                        <div
                            key={template.id}
                            className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-blue-300 transition-all"
                        >
                            {/* Template Header */}
                            <div
                                className="p-4 cursor-pointer flex items-center justify-between"
                                onClick={() => setExpandedTemplate(expandedTemplate === template.id ? null : template.id)}
                            >
                                <div className="flex items-center space-x-3 flex-1">
                                    <Mail className="w-5 h-5 text-blue-600" />
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-gray-900">{template.name}</h3>
                                        <p className="text-sm text-gray-600">
                                            {template.subject || 'No subject'}
                                        </p>
                                    </div>
                                    <div className="flex items-center flex-wrap gap-1.5">
                                        <span className={`text-xs px-2 py-1 rounded font-medium ${template.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                            {template.enabled ? 'Enabled' : 'Disabled'}
                                        </span>
                                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded font-medium">
                                            {triggerEventOptions.find(o => o.value === template.triggerEvent)?.label}
                                        </span>
                                        {template.categoryId ? (
                                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded font-medium">
                                                <Tag className="w-3 h-3" />
                                                {categories.find(c => c.id === template.categoryId)?.name || 'Category'}
                                            </span>
                                        ) : categories.length > 0 ? (
                                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded font-medium">All</span>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2 ml-4">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteTemplate(template.id);
                                        }}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                    {expandedTemplate === template.id ? (
                                        <ChevronUp className="w-5 h-5 text-gray-500" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5 text-gray-500" />
                                    )}
                                </div>
                            </div>

                            {/* Template Details (Expanded) */}
                            {expandedTemplate === template.id && (
                                <div className="border-t border-gray-200 p-6 space-y-4 bg-gray-50">
                                    {/* Template Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Template Name
                                        </label>
                                        <input
                                            type="text"
                                            value={template.name}
                                            onChange={(e) => updateTemplate(template.id, { name: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>

                                    {/* Trigger Event */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Trigger Event
                                        </label>
                                        <select
                                            value={template.triggerEvent}
                                            onChange={(e) => updateTemplate(template.id, { triggerEvent: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            {triggerEventOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Category Filter */}
                                    {categories.length > 0 && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Send to Category
                                            </label>
                                            <select
                                                value={template.categoryId || ''}
                                                onChange={(e) => updateTemplate(template.id, { categoryId: e.target.value || null })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="">All participants (no category filter)</option>
                                                {categories.map(cat => (
                                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {template.categoryId
                                                    ? `Only participants in "${categories.find(c => c.id === template.categoryId)?.name}" will receive this template.`
                                                    : 'Sent to all participants who do not match a category-specific template.'}
                                            </p>
                                        </div>
                                    )}

                                    {/* From */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            From
                                        </label>
                                        <input
                                            type="email"
                                            value={template.from}
                                            onChange={(e) => updateTemplate(template.id, { from: e.target.value })}
                                            placeholder={formData.emailConfig?.email}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>

                                    {/* Recipient Fields (Manual Only) */}
                                    {template.triggerEvent === 'manual' && (
                                        <>
                                            {/* To */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    To (comma-separated)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={template.to.join(', ')}
                                                    onChange={(e) => handleArrayInput(template.id, 'to', e.target.value)}
                                                    placeholder="recipient@example.com, another@example.com"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>

                                            {/* CC */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    CC (comma-separated, optional)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={template.cc.join(', ')}
                                                    onChange={(e) => handleArrayInput(template.id, 'cc', e.target.value)}
                                                    placeholder="cc@example.com"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>

                                            {/* BCC */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    BCC (comma-separated, optional)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={template.bcc.join(', ')}
                                                    onChange={(e) => handleArrayInput(template.id, 'bcc', e.target.value)}
                                                    placeholder="bcc@example.com"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>
                                        </>
                                    )}

                                    {template.triggerEvent !== 'manual' && (
                                        <div className="bg-blue-50 p-4 rounded-lg flex items-start">
                                            <div className="flex-shrink-0">
                                                <AlertCircle className="h-5 w-5 text-blue-400" aria-hidden="true" />
                                            </div>
                                            <div className="ml-3">
                                                <h3 className="text-sm font-medium text-blue-800">Automated Recipient</h3>
                                                <div className="mt-2 text-sm text-blue-700">
                                                    <p>
                                                        This email will be automatically sent to the registered participant's email address.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Subject */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Subject
                                        </label>
                                        <input
                                            type="text"
                                            value={template.subject}
                                            onChange={(e) => updateTemplate(template.id, { subject: e.target.value })}
                                            placeholder="Welcome to {{eventName}}!"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Use {`{{eventName}}`}, {`{{participantName}}`}, {`{{name}}`} for dynamic values
                                        </p>
                                    </div>

                                    {/* ChatGPT Prompt Helper */}
                                    <div className="rounded-xl border border-dashed border-violet-300 bg-violet-50 p-4">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <p className="text-sm font-semibold text-violet-800">Generate with AI</p>
                                                <p className="text-xs text-violet-600 mt-0.5">
                                                    Copy this ready-made prompt and paste it into ChatGPT — it includes your event details, participant fields, and all template variables.
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => copyPrompt(template)}
                                                className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                                                    copiedId === template.id
                                                        ? 'bg-green-600 text-white'
                                                        : 'bg-violet-600 hover:bg-violet-700 text-white'
                                                }`}
                                            >
                                                {copiedId === template.id ? (
                                                    <><Check className="w-4 h-4" /> Copied!</>
                                                ) : (
                                                    <><ClipboardCopy className="w-4 h-4" /> Copy Prompt</>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* HTML Body + Live Preview */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Email Body (HTML)
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => togglePreview(template.id)}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold border transition-colors ${
                                                    previewIds.has(template.id)
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                                                }`}
                                            >
                                                {previewIds.has(template.id)
                                                    ? <><EyeOff className="w-3.5 h-3.5" /> Hide Preview</>
                                                    : <><Eye className="w-3.5 h-3.5" /> Show Preview</>
                                                }
                                            </button>
                                        </div>

                                        <div className={`flex gap-3 ${previewIds.has(template.id) ? 'flex-col sm:flex-row' : ''}`}>
                                            {/* Editor */}
                                            <textarea
                                                value={template.htmlBody}
                                                onChange={(e) => updateTemplate(template.id, { htmlBody: e.target.value })}
                                                placeholder="<p>Hello {{name}},</p><p>Thank you for registering for {{eventName}}!</p>"
                                                rows={previewIds.has(template.id) ? 16 : 8}
                                                className={`px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm resize-none ${
                                                    previewIds.has(template.id) ? 'w-full sm:w-1/2' : 'w-full'
                                                }`}
                                            />

                                            {/* Preview pane */}
                                            {previewIds.has(template.id) && (
                                                <div className="w-full sm:w-1/2 flex flex-col">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Preview</span>
                                                        <span className="text-xs text-gray-400">(template variables shown as-is)</span>
                                                    </div>
                                                    <iframe
                                                        srcDoc={template.htmlBody || '<p style="color:#aaa;font-family:sans-serif;padding:16px">Nothing to preview yet…</p>'}
                                                        title="Email Preview"
                                                        sandbox="allow-same-origin"
                                                        className="flex-1 w-full border border-gray-300 rounded-lg bg-white"
                                                        style={{ minHeight: '320px' }}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        <p className="text-xs text-gray-500 mt-1">
                                            Supports HTML formatting and template variables
                                        </p>
                                    </div>

                                    {/* Text Body */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Plain Text Body (Fallback)
                                        </label>
                                        <textarea
                                            value={template.textBody}
                                            onChange={(e) => updateTemplate(template.id, { textBody: e.target.value })}
                                            placeholder="Hello {{participantName}}, Thank you for registering for {{eventName}}!"
                                            rows={4}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                        />
                                    </div>

                                    {/* Enabled Toggle */}
                                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                        <div>
                                            <h4 className="font-medium text-gray-900">Enable Template</h4>
                                            <p className="text-sm text-gray-600">
                                                Disabled templates won't be sent automatically
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => updateTemplate(template.id, { enabled: !template.enabled })}
                                            className={`
                                                relative inline-flex h-8 w-16 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                                                ${template.enabled ? 'bg-green-600' : 'bg-gray-300'}
                                            `}
                                        >
                                            <span
                                                className={`
                                                    inline-block h-6 w-6 transform rounded-full bg-white transition-transform
                                                    ${template.enabled ? 'translate-x-9' : 'translate-x-1'}
                                                `}
                                            />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Category template explanation */}
            {categories.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex gap-3">
                        <Tag className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
                        <div className="text-sm text-purple-800">
                            <p className="font-semibold mb-1">Per-Category Templates</p>
                            <p>When a participant registers, the system first looks for a template assigned to their category. If none exists, the <strong>All participants</strong> template is used as fallback.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                    <AlertCircle className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                        <p className="font-semibold mb-1">Template Variables:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li><code className="bg-blue-100 px-1 rounded">{`{{participantName}}`}</code> - Participant name (also works as {`{{name}}`})</li>
                            <li><code className="bg-blue-100 px-1 rounded">{`{{registrationId}}`}</code> - Registration ID</li>
                            <li><code className="bg-blue-100 px-1 rounded">{`{{eventName}}`}</code> - Event name</li>
                            <li><code className="bg-blue-100 px-1 rounded">{`{{eventDate}}`}</code> - Event date</li>
                            <li><code className="bg-blue-100 px-1 rounded">{`{{eventAddress}}`}</code> - Event location</li>
                            <li><code className="bg-blue-100 px-1 rounded">{`{{publicLink}}`}</code> - Public ID Card Link</li>
                            {(formData.participantCategories?.length > 0) && (
                                <li><code className="bg-blue-100 px-1 rounded">{`{{categoryName}}`}</code> - Participant category (e.g. Delegate, Speaker)</li>
                            )}

                            {/* Dynamic Fields */}
                            {formData.participantFields?.map(field => (
                                <li key={field.id}>
                                    <code className="bg-blue-100 px-1 rounded">{`{{${field.fieldName}}}`}</code> - {field.fieldName}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailTemplatesStep;
